import {
  Avatar,
  Card,
  CardContent,
  Chip,
  CardHeader,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  TextField,
  Typography,
  Tooltip,
  Button,
  ButtonBase,
  CircularProgress,
} from '@material-ui/core';
import { Folder } from '@material-ui/icons';
import { format } from 'date-fns';
import { useDebounce } from 'hooks';
import { fetchClients } from 'lib/Api/Clients';
import { getUserData } from 'lib/Helper/Auth';
import { stringAvatar } from 'lib/Helper/Avatars';
import { PagedClients } from 'lib/Model/Client';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  resolveMergedAliasesToPlatformNames,
  resolvePlatformName,
} from 'views/Clients/components/SuggestedMatches';
import { ActivityFeed } from './components/ActivityFeed';
import { Alert, Autocomplete } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  toolCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '100%',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create('background-color'),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  toolIcon: {
    width: 20,
    height: 20,
    marginBottom: theme.spacing(0.5),
  },
  toolTitle: {
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
  },
  sectionTitle: {
    padding: theme.spacing(1.5, 2, 0.5),
    fontSize: '1rem',
  },
}));

interface Shortcut {
  link: string;
  title: string;
  description: string;
  icon?: string;
  divider?: boolean;
}

const shortcuts: Shortcut[] = [
  {
    link: 'https://konnekt.talexiohr.com',
    title: 'Talexio HR',
    description: 'Manage your personal details, book leave and more.',
    icon: '/talexio.png',
  },
  {
    link: 'https://konnekt.talexiohr.com/ats/dashboard',
    title: 'Konnekt ATS',
    description:
      'Our applicant tracking system with candidates, vacancies and clients.',
    icon: '/konnekt.png',
  },
  {
    link: 'https://app.clickup.com/43267036/home',
    title: 'ClickUp',
    description: 'Our project management tool.',
    icon: '/clickup.png',
  },
  {
    link: 'https://chat.google.com/',
    title: 'Google Chat',
    description: 'Our chat platform.',
    icon: '/google_chat.png',
  },
  {
    link: 'https://talexiohr.zendesk.com/agent/',
    title: 'Zendesk Support',
    description:
      'Receive and action support tickets submitted from users of all our systems.',
    icon: '/zendesk_support.png',
  },
  {
    link: 'https://konnekttalexio.zendesk.com/',
    title: 'Zendesk Sell',
    description: 'Manage incoming leads, clients and proposals.',
    icon: '/zendesk_sell.png',
  },
  {
    link: 'https://integratedtalent-company.monday.com/',
    title: 'Monday.com',
    description: 'Our project management tool for the Konnekt Payroll team.',
    icon: '/monday.svg',
  },
  {
    link: 'https://preeosoftware.atlassian.net/',
    title: 'JIRA',
    description: 'Keep track of development tasks and issues.',
    icon: '/jira.ico',
  },
  {
    link: 'https://drive.google.com/drive/folders/0ANqsmV1Ux7tQUk9PVA',
    title: 'Client Drive',
    description:
      'Our Google Drive folder for client agreements and meeting notes.',
    icon: '/Google_Drive.png',
  },
  {
    link: 'https://exp42.signrequest.com/',
    title: 'SignRequest',
    description: 'Sign and send terms of business and agreements to clients.',
    icon: '/signrequest.png',
  },
  {
    link: 'https://registry.mbr.mt/ROC/index.jsp#companySearch.do?action=companyDetails',
    title: 'Malta Business Registry',
    description: 'Find information on companies listed on the MBR.',
    icon: '/roca-logo.png',
  },
  {
    link: 'https://vat-search.eu/search/mt',
    title: 'VAT Search',
    description: 'Find the VAT Number of a company or entity.',
    icon: '/vat-search.png',
  },
  {
    link: '/resources',
    title: 'Other Resources',
    description: 'A list of other resources, reports and tools.',
  },
  {
    link: '/dashboards',
    title: 'Dashboards',
    description: 'All our dashboards in one place.',
  },
];

const sections = [
  {
    title: 'HR & Recruitment',
    shortcuts: shortcuts.slice(0, 2),
  },
  {
    title: 'Internal Communication & Tools',
    shortcuts: shortcuts.slice(2, 10),
  },
  {
    title: 'Resources & Others',
    shortcuts: shortcuts.slice(10, 14),
  },
];

export const DashboardView: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const user = getUserData();
  const firstName = user?.name?.split(' ')[0] ?? 'there';
  const now = new Date();
  const greeting =
    now.getHours() < 12
      ? 'Good morning'
      : now.getHours() < 18
      ? 'Good afternoon'
      : 'Good evening';
  const dateLabel = format(now, 'EEEE, d MMMM yyyy');

  const openShortcut = (link: string) => {
    if (/^https?:\/\//.test(link)) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      history.push(link);
    }
  };

  const [textSearch, setTextSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState(false);
  const debouncedTextSearch = useDebounce(textSearch, 300);
  const [data, setData] = React.useState<PagedClients | undefined>();

  const fetchData = React.useCallback(() => {
    if (debouncedTextSearch.length === 0) {
      setData(undefined);
      setSearchError(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    fetchClients(1, 5, debouncedTextSearch)
      .then((d) => {
        setData(d);
      })
      .catch(() => {
        setSearchError(true);
        setData(undefined);
      })
      .finally(() => setLoading(false));
  }, [debouncedTextSearch]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {greeting}, {firstName}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {dateLabel}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <ActivityFeed />
      </Grid>
      <Grid item xs={12} md={4}>
        <Card className={classes.card}>
          <CardHeader
            title="Client Search"
            subheader="Search for clients across different platforms. Click 'Advanced Search' for more results and filters."
            action={
              <Button
                color="secondary"
                onClick={() => history.push('/clients')}
              >
                Advanced Search
              </Button>
            }
          />
          <CardContent>
            <Autocomplete
              freeSolo
              fullWidth
              options={data?.data ?? []}
              filterOptions={(opts) => opts}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.company_title
              }
              loading={loading}
              inputValue={textSearch}
              onInputChange={(_, value, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setTextSearch(value);
                }
              }}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  history.push(`/clients/${value.id}`);
                }
              }}
              noOptionsText={
                debouncedTextSearch.length === 0
                  ? 'Type to search clients'
                  : 'No matching clients'
              }
              renderOption={(client) => (
                <ListItem component="div" disableGutters dense>
                  <ListItemAvatar>
                    <Avatar {...stringAvatar(client.company_title)} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={client.company_title}
                    secondary={
                      <>
                        Sources:&nbsp;
                        {resolvePlatformName(client.id)}
                        {Boolean(client.merge_count) &&
                          resolveMergedAliasesToPlatformNames(
                            client.merged_aliases ?? null,
                          ).length > 0 &&
                          ' and '}
                        {Boolean(client.merge_count) &&
                          resolveMergedAliasesToPlatformNames(
                            client.merged_aliases ?? null,
                          ).length > 0 && (
                            <Tooltip
                              title={
                                <>
                                  {resolveMergedAliasesToPlatformNames(
                                    client.merged_aliases ?? null,
                                  ).map((name, index) => (
                                    <div key={index}>{name}</div>
                                  ))}
                                </>
                              }
                              placement="top"
                            >
                              <Chip
                                label={`${client.merge_count} ${
                                  client.merge_count! > 1 ? 'others' : 'other'
                                }`}
                                size="small"
                              />
                            </Tooltip>
                          )}
                      </>
                    }
                  />
                </ListItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Type here to search..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {data && data.data && data.total_count > 0 && (
              <Typography variant="caption" color="textSecondary">
                Showing {data.data.length} of {data.total_count} result
                {data.total_count !== 1 ? 's' : ''}. Click 'Advanced Search' for
                more.
              </Typography>
            )}
            {searchError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={fetchData}>
                    Retry
                  </Button>
                }
                style={{ marginTop: 8 }}
              >
                Could not load search results. Try again.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Alert severity="warning" className={classes.card}>
          Information found in the intranet is highly confidential.
          <strong> Never share this information</strong> outside of
          KonnektTalexio.
        </Alert>

        <Card className={classes.card}>
          <CardHeader
            title="KonnektTalexio Tools"
            subheader="Quick links to our tools and platforms."
          />
          <CardContent>
            {sections.map((section) => (
              <React.Fragment key={section.title}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {section.title}
                </Typography>
                <Grid container spacing={1}>
                  {section.shortcuts.map((shortcut) => (
                    <Grid item xs={4} sm={3} key={shortcut.link}>
                      <Tooltip title={shortcut.description} placement="top">
                        <ButtonBase
                          focusRipple
                          className={classes.toolCard}
                          aria-label={`${shortcut.title}. ${shortcut.description}`}
                          onClick={() => openShortcut(shortcut.link)}
                        >
                          {shortcut.icon ? (
                            <img
                              src={shortcut.icon}
                              alt=""
                              className={classes.toolIcon}
                            />
                          ) : (
                            <Folder className={classes.toolIcon} />
                          )}
                          <Typography
                            variant="body2"
                            className={classes.toolTitle}
                          >
                            {shortcut.title}
                          </Typography>
                        </ButtonBase>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
                {section !== sections[sections.length - 1] && (
                  <Divider style={{ margin: '16px 0' }} />
                )}
              </React.Fragment>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
